import Button from '@carvana/ds-button';
import { CheckCircleSpot } from '@carvana/ds-icons';
import { icon } from '@carvana/ds-styles';
import { Check } from 'lucide-react';
import React from 'react';
import EventedModal from '|/app/modals/evented-modal';
import { ModalNames } from '|/app/modals/modal-events';
import { ExampleVisualization } from '|/app/purchase/cart/steps/gap-coverage';
import { StepName } from '|/app/services';
import { buildFinancingImpactStripText } from '|/helpers/carvana-care-modal-variants/carvana-care-modal-variants';
import currency from '|/helpers/currency';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import styles from './gap-coverage-modal.module.css';

export interface GapCoverageModalProps {
  isOpen: boolean;
  onClose: () => void;
  impactsFinancingTerms: boolean;
  vehicleModel?: string;
  termMonths?: number;
  monthlyDelta?: number;
  downDelta?: number;
  termDelta?: number;
  fullPrice?: number;
  isFinanced?: boolean;
  experimentName?: string;
  experimentArm?: string | null;
}

const formatWholeDollars = (value: number) =>
  currency(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const GapCoverageModal = ({
  isOpen,
  onClose,
  impactsFinancingTerms,
  vehicleModel = 'vehicle',
  termMonths = 0,
  monthlyDelta = 0,
  downDelta = 0,
  termDelta = 0,
  fullPrice = 0,
  isFinanced = true,
  experimentName,
  experimentArm
}: GapCoverageModalProps) => {
  const navigate = useNavigateToStep();
  const showAsMonthly = isFinanced;

  const modalEventMetadata: Record<string, unknown> = {
    product: 'GAP',
    ...(showAsMonthly
      ? { monthlyPrice: monthlyDelta, downPayment: downDelta, termDelta, termMonths }
      : { fullPrice }),
    ...(experimentName ? { experimentName } : {}),
    ...(experimentArm ? { experimentArm } : {})
  };

  return (
    <EventedModal
      modalHeader=""
      mobileStyle="inset"
      desktopWidth="480px"
      divLine={false}
      contentPadding={false}
      isOpen={isOpen}
      onClose={onClose}
      analyticsId={ModalNames.GapCoverage}
      eventMetadata={modalEventMetadata}
    >
      <div className={styles.container} data-testid="gap-coverage-modal-container">
        <div className={styles.content}>
          <div className={styles.header}>
            <CheckCircleSpot
              size={32}
              color={icon.primary}
              className={styles.headerIcon}
              data-testid="gap-coverage-header-icon"
            />
            <h2 className={styles.headerTitle} data-testid="gap-coverage-heading">
              Your {vehicleModel} is protected with GAP coverage
            </h2>
          </div>

          <div className={styles.costCard} data-testid="gap-coverage-cost-card">
            <div className={styles.costRow}>
              <div className={styles.costLabels}>
                <div className={styles.costLabelPrimary}>Coverage for {termMonths} months</div>
                <div className={styles.costLabelSecondary}>Or until your loan is paid off</div>
              </div>
              <div className={styles.costAmount}>
                {showAsMonthly ? (
                  <>
                    <span className={styles.costAmountStrong}>
                      +{formatWholeDollars(monthlyDelta)}
                    </span>
                    <span className={styles.costAmountSuffix}>/mo</span>
                  </>
                ) : (
                  <span className={styles.costAmountStrong} data-testid="gap-coverage-full-price">
                    {formatWholeDollars(fullPrice)}
                  </span>
                )}
              </div>
            </div>
            {showAsMonthly && impactsFinancingTerms && (
              <div className={styles.termsChanged} data-testid="gap-coverage-terms-changed">
                {buildFinancingImpactStripText({ downDelta, termDelta })}
              </div>
            )}
          </div>

          <ul className={styles.bulletList} data-testid="gap-coverage-bullets">
            <li className={styles.bulletItem}>
              <Check
                size={16}
                color={icon.primary}
                className={styles.bulletIcon}
                data-testid="check-icon"
              />
              <span className={styles.bulletText}>
                Helps reduce out-of pocket costs if your car is declared a total loss due to an
                accident or theft*
              </span>
            </li>
            <li className={styles.bulletItem}>
              <Check
                size={16}
                color={icon.primary}
                className={styles.bulletIcon}
                data-testid="check-icon"
              />
              <span className={styles.bulletText}>
                Can cover the difference between insurance payout and loan balance*
              </span>
            </li>
            <li className={styles.bulletItem}>
              <Check
                size={16}
                color={icon.primary}
                className={styles.bulletIcon}
                data-testid="check-icon"
              />
              <span className={styles.bulletText}>Cancel anytime for a partial refund</span>
            </li>
          </ul>

          <p className={styles.disclaimer} data-testid="gap-coverage-disclaimer">
            *Refer to your Gap Addendum for refund details and limitations, and for total loss
            details.
          </p>

          <div data-testid="gap-coverage-example">
            <ExampleVisualization />
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="tertiary"
            size="md"
            width="100%"
            data-testid="edit-gap-coverage-button"
            data-analytics-id="pp_gap_coverage_modal_edit"
            onClick={() => {
              navigate(StepName.Gap);
            }}
          >
            Edit
          </Button>
          <Button
            variant="primary"
            size="md"
            width="100%"
            data-testid="close-gap-coverage-button"
            data-analytics-id="pp_gap_coverage_modal_close"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default GapCoverageModal;
